export type BillingCycle = "monthly" | "yearly";

export type PlanPricing = {
  /** Per-month headline figure for the selected cycle, pre-promo. */
  monthlyEquivalent: number;
  /** Total charged for one cycle, pre-promo (monthly→price_monthly, yearly→price_yearly). */
  cycleTotal: number;
  /** Annual total ("Totale €X/anno") — yearly cycle only, else null. */
  annualTotal: number | null;
  /** Yearly money saved vs paying monthly for a year — yearly cycle only, else null. */
  yearlySaving: number | null;
  /** Headline promo: struck pre-promo + discounted price. null when no active promo. */
  promo: { strike: number; promo: number } | null;
  /** Discounted cycle total when a promo applies, else null. */
  cycleTotalPromo: number | null;
};

export type PlanPricingInput = {
  priceMonthly: string | number | null | undefined;
  priceYearly: string | number | null | undefined;
  cycle: BillingCycle;
  /** From the campaign endpoint — pass only when `isPromoActive(slug)` for this user. */
  discountPercent?: number | null;
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

const toMoneyNumber = (
  value: string | number | null | undefined,
): number | null => {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
};

const normalizeDiscount = (
  discountPercent: number | null | undefined,
): number | null => {
  if (typeof discountPercent !== "number") return null;
  if (!Number.isFinite(discountPercent)) return null;
  if (discountPercent <= 0 || discountPercent >= 100) return null;
  return discountPercent;
};

/**
 * Single source of pricing math for the sales surfaces.
 *
 * Derives everything from the real backend `price_monthly` / `price_yearly`
 * (no hardcoded ratio, SPEC B.3 / D16) and, when an active per-user promo applies
 * (`discountPercent` from `GET /payments/campaign`, T0), the struck pre-promo price
 * and the discounted price (`× (1 − discount/100)`).
 *
 * Returns `null` when the price needed for the selected cycle is missing/invalid,
 * so callers degrade to "no price/no savings" instead of rendering a wrong number.
 */
export const computePlanPricing = ({
  priceMonthly,
  priceYearly,
  cycle,
  discountPercent,
}: PlanPricingInput): PlanPricing | null => {
  const monthly = toMoneyNumber(priceMonthly);
  const yearly = toMoneyNumber(priceYearly);

  let monthlyEquivalent: number;
  let cycleTotal: number;
  let annualTotal: number | null;
  let yearlySaving: number | null;

  if (cycle === "yearly") {
    if (yearly === null) return null;
    monthlyEquivalent = round2(yearly / 12);
    cycleTotal = yearly;
    annualTotal = yearly;
    yearlySaving =
      monthly !== null && monthly * 12 - yearly > 0
        ? round2(monthly * 12 - yearly)
        : null;
  } else {
    if (monthly === null) return null;
    monthlyEquivalent = round2(monthly);
    cycleTotal = monthly;
    annualTotal = null;
    yearlySaving = null;
  }

  const discount = normalizeDiscount(discountPercent);
  const factor = discount !== null ? 1 - discount / 100 : null;

  const promo =
    factor !== null
      ? { strike: monthlyEquivalent, promo: round2(monthlyEquivalent * factor) }
      : null;
  const cycleTotalPromo = factor !== null ? round2(cycleTotal * factor) : null;

  return {
    monthlyEquivalent,
    cycleTotal,
    annualTotal,
    yearlySaving,
    promo,
    cycleTotalPromo,
  };
};
